import { ThunkDispatch } from '@reduxjs/toolkit';
import React from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { Helmet } from 'react-helmet';
import { RouteComponentProps, Link, navigate } from '@reach/router';

import { SpaceBetween } from 'utils/style/extendable';
import { appDispatch, ITasks, RootState } from 'store/store';
import * as taskManipulate from 'store/tasks/taskManipulate';

import messages from './Messages';
import Icon from 'components/Icon';
import Button from 'components/Button';
import LogoLoading from 'components/LogoLoading';
import { display } from 'components/ErrorView/ErrorDialog';
import notify from 'components/Notify';
import Tooltip from 'components/Tooltip';
import { IUsers } from 'store/users/usersType';
import { ICompany, ICompanyTemplate } from 'store/companies/companiesTypes';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import {
  updateTemplate,
  updateTemplateFn,
} from 'store/companies/companiesSlice';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import {
  isAdvanceAdmin,
  isCompanyOwner,
  isCompanySupport,
} from 'store/companies/companiesUtils';
import { ITask } from 'store/tasks/tasksType';
import i18n, { languageDirection } from '../../../i18n';
import { black, gray } from 'utils/style/colors';

interface IStateToProps {
  tasks: ITasks;
  users: IUsers;
  company: ICompany;
  thisUser: IThisUserState;
}

interface IDispatchToProps {
  updateTemplate: (
    companyTask: ICompanyTemplate
  ) => ReturnType<typeof updateTemplateFn>;
  requireSeveralUser: (
    usersList: number[]
  ) => ReturnType<typeof requireSeveralUserFn>;
}

interface IOwnProps {
  companyId?: string;
}

type IComponentProps = IOwnProps &
  IStateToProps &
  IDispatchToProps &
  RouteComponentProps;

interface IComponentStates {
  loading: boolean;
  showTemplateEditor: boolean;
  archivedTasks: boolean;
  disableSave: boolean;
}

class CompanyTemplatesList extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      loading: true,
      showTemplateEditor: false,
      archivedTasks: false,
      disableSave: false,
    };
  }

  componentDidMount() {
    if (
      !isCompanyOwner(this.props.thisUser, this.props.company) &&
      !isCompanySupport(this.props.thisUser, this.props.company) &&
      !isAdvanceAdmin(this.props.thisUser, this.props.company)
    ) {
      navigate('/notfound');
    }
    const members = Object.keys(this.props.company.members).map((it) =>
      Number(it)
    );
    this.props
      .requireSeveralUser(members)
      .then(() => {
        this.setState({ loading: false });
      })
      .catch((err: any) => {
        display(err);
      });
  }

  handleArchiveTemplate = (template: ITask) => {
    const newTask = taskManipulate.storeToServer(template, true);
    newTask.archived = !newTask.archived;
    const newTemplate: ICompanyTemplate = {
      task: newTask,
      taskId: template.id,
      companyId: Number(this.props.companyId),
      suggestOnDocumentManager: false,
    };
    this.props
      .updateTemplate(newTemplate)
      .then(() => {
        notify(messages.templateArchived(newTask.archived) as string);
        this.setState({ archivedTasks: false });
      })
      .catch((err: any) => {
        display(err);
      });
  };

  handleClickCreateButton = () => {
    const companyId = Number(this.props.companyId);
    navigate(`/t/${companyId}/admin/template/editor`);
  };

  showArchivedTask = (state: boolean) => {
    this.setState({ archivedTasks: state });
  };

  render() {
    if (this.state.loading) {
      return <LogoLoading />;
    }
    const { company, tasks } = this.props;
    const companyId = Number(this.props.companyId);
    const companyTemplates = Object.values(tasks.data).filter((task: ITask) =>
      Object.keys(company.templates).includes(task.id.toString())
    );
    const templates = companyTemplates.filter(
      (task: ITask) => task.archived === this.state.archivedTasks
    );

    return (
      <React.Fragment>
        <Helmet>
          <title>
            {i18n.t('companyTemplates', { ns: 'companyManagement' }) as string}
          </title>
          <meta
            name="description"
            content={
              i18n.t('companyTemplates', { ns: 'companyManagement' }) as string
            }
          />
        </Helmet>
        <div>
          <SpaceBetween>
            <Button color="green" onClick={this.handleClickCreateButton}>
              {
                i18n.t('createNewTemplate', {
                  ns: 'companyManagement',
                }) as string
              }
            </Button>
            {companyTemplates.find((task: ITask) => task.archived) && (
              <React.Fragment>
                {this.state.archivedTasks ? (
                  <a
                    href="#!"
                    onClick={(e) => {
                      e.preventDefault();
                      this.showArchivedTask(false);
                    }}
                  >
                    {
                      i18n.t('showUnArchivedTemplates', {
                        ns: 'companyManagement',
                      }) as string
                    }
                  </a>
                ) : (
                  <a
                    href="#!"
                    onClick={(e) => {
                      e.preventDefault();
                      this.showArchivedTask(true);
                    }}
                    style={{ color: black }}
                  >
                    {
                      i18n.t('showArchivedTemplates', {
                        ns: 'companyManagement',
                      }) as string
                    }
                  </a>
                )}
              </React.Fragment>
            )}
          </SpaceBetween>

          <div className="row">
            {templates.length === 0 ? (
              <span>
                {
                  i18n.t('thereIsNoTemplateYet', {
                    ns: 'companyManagement',
                  }) as string
                }
              </span>
            ) : (
              templates
                .sort((a, b) => {
                  if (b.taskName < a.taskName) {
                    return 1;
                  }
                  return 0;
                })
                .map((template) => (
                  <div
                    key={template.id}
                    className="col s6"
                    style={{
                      float: languageDirection() === 'rtl' ? 'right' : 'left',
                      color: black,
                    }}
                  >
                    <div
                      className="card"
                      style={{
                        borderRadius: '1rem',
                        overflow: 'hidden',
                        background: gray,
                      }}
                    >
                      <div className="card-content">
                        <Tooltip text={template.taskName}>
                          <span className="card-title text-ellipsis">
                            {template.taskName}
                          </span>
                        </Tooltip>
                      </div>
                      <div className="card-action">
                        {template.archived ? (
                          <React.Fragment>
                            <span className="orange-text text-accent-2">
                              {
                                i18n.t('archived', {
                                  ns: 'companyManagement',
                                }) as string
                              }
                            </span>
                            <span
                              style={{ cursor: 'pointer' }}
                              className="left orange-text text-accent-2"
                              onClick={() =>
                                this.handleArchiveTemplate(template)
                              }
                            >
                              <Icon type={'upload'} />
                            </span>
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            <Link
                              to={`/t/${companyId}/admin/template/editor/${template.id}`}
                            >
                              <Icon type={'edit-2'} />
                            </Link>
                            <span
                              style={{ cursor: 'pointer' }}
                              className="left orange-text text-accent-2"
                              onClick={() =>
                                this.handleArchiveTemplate(template)
                              }
                            >
                              <Icon type={'archive'} />
                            </span>
                          </React.Fragment>
                        )}
                      </div>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const companyId = Number(props.companyId);
  const tasks = state.tasks;
  const company = state.companies.data[companyId];
  const thisUser = state.thisUser;
  const users = state.users;
  return { tasks, company, thisUser, users };
};

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = (
  dispatch: ThunkDispatch<any, never, any>
) => ({
  updateTemplate: (companyTask: ICompanyTemplate) =>
    appDispatch(updateTemplate({ companyTemplate: companyTask })),
  requireSeveralUser: (usersList: number[]) =>
    dispatch(requireSeveralUser({ usersList })).unwrap(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CompanyTemplatesList);
